// Lyrics Service
// Fetches time-synced lyrics from lrclib.net (free, no API key, CORS-enabled)
// and exposes them to the canvas visualizer via a module-level store so the
// draw loop can read them without prop drilling through React.

const LRCLIB_API = 'https://lrclib.net/api/get';
const LRCLIB_SEARCH_API = 'https://lrclib.net/api/search';
// lrclib can take 5-10s to respond under load; a short timeout turned every
// slow response into "no lyrics" (the '♪ instrumental vibes ♪' fallback)
const FETCH_TIMEOUT_MS = 20000;

/**
 * Strip Spotify-style title decorations that make lrclib's exact-match
 * lookup miss: "(feat. X)", "[with Y]", "- 2011 Remaster", "- Radio Edit"…
 */
export function cleanTrackTitle(track) {
  return (track || '')
    .replace(/\s*[([][^)\]]*\b(feat\.?|ft\.?|with)\b[^)\]]*[)\]]/gi, '')
    .replace(/\s+-\s+[^-]*\b(remaster(ed)?|version|edit|mix|remix|live|mono|stereo|deluxe|single|bonus|acoustic|demo)\b.*$/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Module-level store read by the Lyric Flow visualizer every frame
let lyricsState = {
  status: 'none',   // 'none' | 'loading' | 'synced' | 'plain' | 'unavailable'
  trackKey: null,
  lines: [],        // [{ t: seconds, text: string }] sorted by t (synced only)
  plainText: null   // fallback when only unsynced lyrics exist
};

// Cache fetched lyrics per track for the session (avoids refetch on replay)
const lyricsCache = new Map();
const CACHE_MAX = 50;

function trackKey(artist, track) {
  return `${(artist || '').toLowerCase().trim()}|${(track || '').toLowerCase().trim()}`;
}

/**
 * Parse LRC format ("[mm:ss.xx] words") into sorted {t, text} lines.
 * Lines with empty text are kept as gaps so the visualizer can breathe
 * between verses instead of holding the last line forever.
 */
export function parseLRC(lrc) {
  const lines = [];
  const timeTag = /\[(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?\]/g;

  for (const raw of lrc.split('\n')) {
    const text = raw.replace(timeTag, '').trim();
    timeTag.lastIndex = 0;
    let match;
    while ((match = timeTag.exec(raw)) !== null) {
      const minutes = parseInt(match[1], 10);
      const seconds = parseInt(match[2], 10);
      const fraction = match[3] ? parseInt(match[3].padEnd(3, '0'), 10) / 1000 : 0;
      lines.push({ t: minutes * 60 + seconds + fraction, text });
    }
  }

  return lines.sort((a, b) => a.t - b.t);
}

export function clearLyrics() {
  lyricsState = { status: 'none', trackKey: null, lines: [], plainText: null };
}

export function getLyricsState() {
  return lyricsState;
}

/**
 * Fetch lyrics for a track and load them into the store.
 * Fire-and-forget from the track-change handler; safe to call repeatedly
 * (stale responses are dropped if the track changed mid-fetch).
 */
export async function loadLyricsForTrack(artist, track, durationSec) {
  if (!artist || !track) return;

  const key = trackKey(artist, track);
  if (lyricsState.trackKey === key && lyricsState.status !== 'none') return;

  const cached = lyricsCache.get(key);
  if (cached) {
    lyricsState = { ...cached, trackKey: key };
    return;
  }

  lyricsState = { status: 'loading', trackKey: key, lines: [], plainText: null };

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    const getJson = async (url, params) => {
      const response = await fetch(`${url}?${params}`, { signal: controller.signal });
      if (!response.ok) return null;
      return response.json();
    };

    // 1. Exact lookup with duration (lrclib 404s on duration mismatch)
    const params = new URLSearchParams({ artist_name: artist, track_name: track });
    if (durationSec) params.set('duration', String(Math.round(durationSec)));
    let data = await getJson(LRCLIB_API, params);

    // 2. Exact lookup without duration
    if (!data && durationSec) {
      params.delete('duration');
      data = await getJson(LRCLIB_API, params);
    }

    // 3. Cleaned title (strips "(feat. X)", "- Remaster", …)
    const cleaned = cleanTrackTitle(track);
    if (!data && cleaned && cleaned !== track) {
      params.set('track_name', cleaned);
      data = await getJson(LRCLIB_API, params);
    }

    // 4. Fuzzy search — pick the best hit: synced lyrics preferred, and if we
    // know the duration, it must be within 10s of the Spotify track
    if (!data) {
      const searchParams = new URLSearchParams({
        track_name: cleaned || track,
        artist_name: artist
      });
      const results = await getJson(LRCLIB_SEARCH_API, searchParams);
      if (Array.isArray(results) && results.length > 0) {
        const durationOk = (r) =>
          !durationSec || !r.duration || Math.abs(r.duration - durationSec) <= 10;
        data =
          results.find(r => r.syncedLyrics && durationOk(r)) ||
          results.find(r => r.plainLyrics && durationOk(r)) ||
          null;
      }
    }
    clearTimeout(timer);

    if (lyricsState.trackKey !== key) return; // track changed mid-fetch

    let next;
    if (data?.syncedLyrics) {
      next = { status: 'synced', lines: parseLRC(data.syncedLyrics), plainText: null };
    } else if (data?.plainLyrics) {
      next = { status: 'plain', lines: [], plainText: data.plainLyrics };
    } else {
      next = { status: 'unavailable', lines: [], plainText: null };
    }

    lyricsState = { ...next, trackKey: key };
    if (lyricsCache.size >= CACHE_MAX) {
      lyricsCache.delete(lyricsCache.keys().next().value);
    }
    lyricsCache.set(key, next);
    console.log(`🎤 Lyrics ${next.status} for ${artist} - ${track}` +
      (next.status === 'synced' ? ` (${next.lines.length} lines)` : ''));
  } catch (error) {
    if (lyricsState.trackKey === key) {
      lyricsState = { status: 'unavailable', trackKey: key, lines: [], plainText: null };
    }
    console.warn('🎤 Lyrics fetch failed:', error.message);
  }
}

/**
 * Find the active synced line for a playback time.
 * Returns { index, line, start, end, prev, next } or null when before the
 * first line / no synced lyrics.
 */
export function getLyricAt(time) {
  const { status, lines } = lyricsState;
  if (status !== 'synced' || lines.length === 0) return null;

  let index = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].t <= time) index = i;
    else break;
  }
  if (index === -1) return null;

  const line = lines[index];
  const nextLine = lines[index + 1] || null;
  return {
    index,
    line,
    start: line.t,
    end: nextLine ? nextLine.t : line.t + 5,
    prev: lines[index - 1] || null,
    next: nextLine
  };
}
